import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sun, Moon, BookOpen, ArrowRight } from 'lucide-react';

export default function SignupPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { signup } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            await signup(email, password, name);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-split">
            {/* Image Side */}
            <div className="login-image-side">
                <img
                    src="https://images.unsplash.com/photo-1547459671-6d95eed7af1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxsaWJyYXJ5JTIwYXJjaGl0ZWN0dXJlJTIwc3R1ZHl8ZW58MHx8fHwxNzc0MzA4Mzc1fDA&ixlib=rb-4.1.0&q=85"
                    alt="Architectural library"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20 noise-overlay" />
                <div className="absolute bottom-12 left-12 right-12">
                    <h1 className="font-serif text-4xl lg:text-5xl text-white tracking-tight mb-4">
                        Start your<br />journey.
                    </h1>
                    <p className="font-sans text-white/80 text-lg">
                        Create flashcard sets and master any subject.
                    </p>
                </div>
            </div>

            {/* Form Side */}
            <div className="login-form-side relative">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleTheme}
                    data-testid="theme-toggle-signup"
                    className="absolute top-6 right-6"
                >
                    {theme === 'light' ? (
                        <Moon className="w-5 h-5" strokeWidth={1.5} />
                    ) : (
                        <Sun className="w-5 h-5" strokeWidth={1.5} />
                    )}
                </Button>

                <div className="w-full max-w-sm">
                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-10 h-10 bg-primary flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-primary-foreground" strokeWidth={1.5} />
                        </div>
                        <span className="font-serif text-2xl tracking-tight">flashKard</span>
                    </div>

                    <h2 className="font-serif text-3xl tracking-tight mb-2">Create account</h2>
                    <p className="font-sans text-muted-foreground mb-8">
                        Start memorizing smarter today
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div 
                                data-testid="signup-error"
                                className="p-4 bg-destructive/10 border border-destructive/30 text-destructive text-sm"
                            >
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="name" className="font-mono text-xs tracking-wider uppercase">
                                Name
                            </Label>
                            <Input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name"
                                required
                                data-testid="signup-name-input"
                                className="rounded-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-mono text-xs tracking-wider uppercase">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                data-testid="signup-email-input"
                                className="rounded-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="font-mono text-xs tracking-wider uppercase">
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 6 characters"
                                required
                                data-testid="signup-password-input"
                                className="rounded-none"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="signup-submit-button"
                            className="w-full rounded-none btn-lift h-12"
                        >
                            {loading ? 'Creating account...' : 'Create account'}
                            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                        </Button>
                    </form>

                    <p className="mt-8 text-center text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <Link 
                            to="/login" 
                            data-testid="login-link"
                            className="text-primary hover:underline underline-offset-4"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
